import { ReactNode, useCallback, useMemo } from 'react';

import { PermissionsContext } from '@/shared/features/permissions/context/permissions-context';
import { selectPermissionResources } from '@/shared/features/permissions/store/permissions.slice';
import { buildPermissionModel } from '@/shared/features/permissions/utils/build-permission-model';
import { TPermissionResources } from '@/shared/features/permissions/utils/build-permission-model.types';
import { useReadAllResourceGroupsWithoutPermissionChecksQuery } from '@/shared/redux/api/assets/groups/groups-api';
import { selectUser } from '@/shared/redux/api/auth/auth-api';
import { useReadPoliciesForOwnUserQuery } from '@/shared/redux/api/ident/ident-api';
import { useAppSelector } from '@/shared/redux/hooks/hooks';

// Progressive, non-blocking: the model starts from policies (+ the group-ancestry tree)
// so action-presence checks work immediately; id/group/tag scopes resolve as the harvest
// listener fills the store from the lists the app fetches. No FullscreenLoader block.
export const ConsolePermissionsProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const user = useAppSelector(selectUser);
  const skip = !user;

  const {
    data: policiesData,
    error: policiesError,
    isFetching: isPoliciesFetching,
    isLoading: isPoliciesLoading,
    refetch: refetchPolicies,
  } = useReadPoliciesForOwnUserQuery({}, { skip });
  const {
    data: groupsData,
    error: groupsError,
    isFetching: isGroupsFetching,
    isLoading: isGroupsLoading,
    refetch: refetchGroups,
  } = useReadAllResourceGroupsWithoutPermissionChecksQuery({}, { skip });
  const resources = useAppSelector(selectPermissionResources);

  const isLoading =
    isPoliciesLoading ||
    isPoliciesFetching ||
    isGroupsLoading ||
    isGroupsFetching;
  const error =
    policiesData === undefined && policiesError
      ? policiesError
      : groupsData === undefined && groupsError
        ? groupsError
        : undefined;
  const retry = useCallback(() => {
    void refetchPolicies();
    void refetchGroups();
  }, [refetchGroups, refetchPolicies]);

  const model = useMemo(() => {
    const policies =
      policiesData?.readUserRolesForOwnUser?.items?.flatMap(
        ({ policies }) => policies,
      ) ?? [];

    const groups =
      groupsData?.readAllResourceGroupsForPermissions?.map(
        ({ groupId, parentGroupId }) => ({ groupId, parentGroupId }),
      ) ?? [];

    const resourceItems: TPermissionResources = Object.fromEntries(
      Object.entries(resources).map(([entity, byId]) => [
        entity,
        Object.entries(byId).map(([id, entry]) => ({
          id,
          groupId: entry.groupId,
          tags: entry.tags,
        })),
      ]),
    );

    // Returned list/detail items are backend-filtered, so harvested ids can
    // positively satisfy read checks. A missing harvested id is not proof of
    // denial in console because harvesting is progressive.
    return buildPermissionModel({
      policies,
      resources: resourceItems,
      groups,
      readableIdsAuthoritative: false,
    });
  }, [policiesData, groupsData, resources]);

  return (
    <PermissionsContext.Provider value={{ model, isLoading, error, retry }}>
      {children}
    </PermissionsContext.Provider>
  );
};
